import { formatDate } from "@plasma/utils/time";
import { type ColumnDef, createColumnHelper } from "@tanstack/react-table";
import { useQuery } from "convex/react";
import { useMemo } from "react";

import {
  type EmployeeTipEntry,
  locationSalesGetEmployeeTips,
} from "~/api-refs";
import { SalesDataTable } from "~/components/reports/sales-data-table";
import { LoadingPanel } from "~/components/shared/loading-panel";
import { toConvexTableId } from "~/lib/convex-table-id";

type TipsData = EmployeeTipEntry;

interface TipsViewProps {
  locationId: string;
  employeeId: string;
  dateRange: {
    from: Date;
    to: Date;
  };
  tableId: string;
}

export const TipsView = ({
  locationId,
  employeeId,
  dateRange,
  tableId,
}: TipsViewProps) => {
  const data = useQuery(locationSalesGetEmployeeTips, {
    dateRange: {
      from: dateRange.from.getTime(),
      to: dateRange.to.getTime(),
    },
    locationId: toConvexTableId("locations", locationId),
    employeeId: toConvexTableId("employees", employeeId),
  });

  const columnHelper = createColumnHelper<TipsData>();

  const columns = useMemo(
    () =>
      [
        columnHelper.accessor("location", {
          header: "Location",
          cell: ({ getValue }) => getValue().name,
          meta: {
            getCellForExport: (r: TipsData["location"]) => r.name,
          },
        }),
        columnHelper.accessor("date", {
          header: "Date",
          cell: ({ getValue }) => formatDate(new Date(getValue()), "M/D/YYYY"),
          meta: {
            getCellForExport: (d: TipsData["date"]) =>
              new Date(d).toISOString(),
          },
        }),
        columnHelper.accessor("amountSold", {
          header: "Amount Sold",
          cell: ({ getValue }) =>
            getValue()?.toLocaleString("en-US", {
              style: "currency",
              currency: "USD",
            }),
          meta: {
            getCellForExport: (d: TipsData["amountSold"]) => d,
          },
        }),
        columnHelper.accessor("cashTips", {
          header: "Cash Tips",
          cell: ({ getValue }) =>
            getValue()?.toLocaleString("en-US", {
              style: "currency",
              currency: "USD",
            }),
          meta: {
            getCellForExport: (d: TipsData["cashTips"]) => d,
          },
        }),
        columnHelper.accessor("creditTips", {
          header: "Credit Tips",
          cell: ({ getValue }) =>
            getValue()?.toLocaleString("en-US", {
              style: "currency",
              currency: "USD",
            }),
          meta: {
            getCellForExport: (d: TipsData["creditTips"]) => d,
          },
        }),
        columnHelper.accessor("debitTips", {
          header: "Debit Tips",
          cell: ({ getValue }) =>
            getValue()?.toLocaleString("en-US", {
              style: "currency",
              currency: "USD",
            }),
          meta: {
            getCellForExport: (d: TipsData["debitTips"]) => d,
          },
        }),
        columnHelper.accessor("totalTips", {
          header: "Total Tips",
          cell: ({ getValue }) =>
            getValue()?.toLocaleString("en-US", {
              style: "currency",
              currency: "USD",
            }),
          meta: {
            getCellForExport: (d: TipsData["totalTips"]) => d,
          },
        }),
        columnHelper.display({
          id: "tipPercentage",
          header: "Tip %",
          cell: ({ row }) => {
            const amountSold = row.original.amountSold;
            const totalTips = row.original.totalTips;
            if (!amountSold || amountSold === 0) {
              return "0.0%";
            }
            const tipPercentage = (totalTips / amountSold) * 100;
            return `${tipPercentage.toFixed(1)}%`;
          },
          meta: {
            getCellForExport: (row: TipsData) => {
              const amountSold = row.amountSold;
              const totalTips = row.totalTips;
              if (!amountSold || amountSold === 0) {
                return 0;
              }
              return (totalTips / amountSold) * 100;
            },
          },
        }),
      ] as ColumnDef<TipsData>[],
    [columnHelper]
  );

  if (data === undefined) {
    return <LoadingPanel message="Loading tips data..." />;
  }

  return <SalesDataTable columns={columns} data={data} id={tableId} />;
};
